import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddContractorDialog } from "@/components/AddContractorDialog";
import { AssignJobToContractorDialog } from "@/components/AssignJobToContractorDialog";
import { CONTRACTOR_STATUSES, COMPLIANCE_STATUSES, AVAILABILITY_STATUSES, TRADES, COVERAGE_STATES } from "@/lib/constants";
import { Loader2, Users, Plus, Phone, Mail, Star, Briefcase, Pencil, UserCog, Search, Eye, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const SORTS = [["newest", "Newest"], ["rating", "Highest Rated"], ["jobs", "Most Jobs Completed"], ["name", "Name"]];

export default function Contractors() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [assignItem, setAssignItem] = useState(null);
  const [q, setQ] = useState("");
  const [ft, setFt] = useState({ trade: "all", state: "all", status: "all", compliance: "all", availability: "all", rating: "all", sort: "newest" });

  const load = () => api.get("/admin/contractors").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const setF = (k) => (v) => setFt((p) => ({ ...p, [k]: v }));

  const changeStatus = async (c, status) => {
    try { await api.put(`/admin/contractors/${c.id}/status`, { application_status: status }); load(); toast.success(`Status set to ${status}`); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    let list = (rows || []).filter((c) => {
      const hay = `${c.first_name} ${c.last_name} ${c.company_name} ${c.trade} ${c.city} ${c.email} ${c.phone}`.toLowerCase();
      if (q && !hay.includes(q.toLowerCase())) return false;
      if (ft.trade !== "all" && c.trade !== ft.trade && !(c.trades || []).includes(ft.trade)) return false;
      if (ft.state !== "all" && c.state !== ft.state && !(c.coverage_states || []).includes(ft.state)) return false;
      if (ft.status !== "all" && c.application_status !== ft.status) return false;
      if (ft.compliance !== "all" && c.compliance_status !== ft.compliance) return false;
      if (ft.availability !== "all" && (c.availability_status || "Available") !== ft.availability) return false;
      if (ft.rating !== "all" && (c.admin_rating || 0) < Number(ft.rating)) return false;
      return true;
    });
    const s = ft.sort;
    list = [...list].sort((a, b) => {
      if (s === "rating") return (b.admin_rating || 0) - (a.admin_rating || 0);
      if (s === "jobs") return (b.jobs_completed || 0) - (a.jobs_completed || 0);
      if (s === "name") return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return list;
  }, [rows, q, ft]);

  const complianceTone = (s) => ({ Compliant: "text-green-700 bg-green-50 border-green-200", "Needs Documents": "text-amber-700 bg-amber-50 border-amber-200", "Expired Documents": "text-rose-700 bg-rose-50 border-rose-200", "Under Review": "text-blue-700 bg-blue-50 border-blue-200" }[s] || "text-slate-600 bg-slate-50 border-slate-200");

  return (
    <DashboardLayout nav={getNav(user.role)} title="Contractors">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="contractor-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, trade, city…" className="pl-9" />
        </div>
        <Button data-testid="add-contractor-btn" onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1.5" /> Add Contractor</Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <FilterSelect testid="filter-trade" value={ft.trade} onChange={setF("trade")} placeholder="All Trades" options={TRADES} />
        <FilterSelect testid="filter-state" value={ft.state} onChange={setF("state")} placeholder="All States" options={COVERAGE_STATES} />
        <FilterSelect testid="filter-status" value={ft.status} onChange={setF("status")} placeholder="All Statuses" options={CONTRACTOR_STATUSES} />
        <FilterSelect testid="filter-compliance" value={ft.compliance} onChange={setF("compliance")} placeholder="All Compliance" options={COMPLIANCE_STATUSES} />
        <FilterSelect testid="filter-availability" value={ft.availability} onChange={setF("availability")} placeholder="All Availability" options={AVAILABILITY_STATUSES} />
        <FilterSelect testid="filter-rating" value={ft.rating} onChange={setF("rating")} placeholder="Any Rating" options={["4", "3", "2"]} labelFn={(v) => `${v}+ stars`} />
        <Select value={ft.sort} onValueChange={setF("sort")}><SelectTrigger data-testid="filter-sort" className="w-40"><SelectValue /></SelectTrigger><SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
      </div>

      {!rows ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        rows.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="contractors-empty">
            <Users className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-500">No contractors added yet. Add your first contractor to begin building your field team.</p>
            <Button data-testid="empty-add-contractor-btn" onClick={() => setAddOpen(true)} className="mt-4 bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1.5" /> Add Contractor</Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200" data-testid="contractors-no-match"><p className="text-slate-500">No contractors match your filters.</p></div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="contractor-grid">
            {filtered.map((c) => (
              <div key={c.id} data-testid={`contractor-card-${c.id}`} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col">
                <button onClick={() => navigate(`/app/dispatch/contractors/${c.id}`)} className="text-left" data-testid={`contractor-open-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold shrink-0">{(c.first_name?.[0] || "") + (c.last_name?.[0] || "")}</div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusBadge status={c.application_status} />
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${complianceTone(c.compliance_status)}`} data-testid={`compliance-${c.id}`}>{c.compliance_status}</span>
                    </div>
                  </div>
                  <h3 className="mt-3 font-head font-semibold text-slate-900">{c.first_name} {c.last_name}</h3>
                  <p className="text-sm text-slate-500">{c.company_name || "—"}</p>
                  <p className="mt-1 text-xs font-medium text-blue-600 flex items-center gap-1"><Briefcase className="w-3 h-3" />{c.trade} · {c.city}, {c.state}</p>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <Mini l="Avail." v={c.availability_status || "Available"} />
                    <Mini l="Jobs" v={c.jobs_completed ?? 0} />
                    <Mini l="Rating" v={c.admin_rating ? <span className="inline-flex items-center gap-0.5">{c.admin_rating}<Star className="w-3 h-3 text-amber-500 fill-amber-500" /></span> : "—"} />
                  </div>
                  <div className="mt-3 text-xs text-slate-500 space-y-1">
                    <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone || "—"}</div>
                    <div className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{c.email}</div>
                  </div>
                </button>
                <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-testid={`actions-${c.id}`}>
                  <Button variant="outline" className="h-9 w-full sm:w-auto justify-center text-sm" data-testid={`view-${c.id}`} onClick={() => navigate(`/app/dispatch/contractors/${c.id}`)}>
                    <Eye className="w-4 h-4 mr-1.5" />View
                  </Button>
                  {(() => {
                    const reason = assignDisabledReason(c);
                    return (
                      <span title={reason || "Assign a work order"} className="w-full sm:w-auto">
                        <Button variant="outline" disabled={!!reason} className="h-9 w-full sm:w-auto justify-center text-sm" data-testid={`assign-${c.id}`} onClick={() => setAssignItem(c)}>
                          <ClipboardList className="w-4 h-4 mr-1.5" />Assign
                        </Button>
                      </span>
                    );
                  })()}
                  {c.phone
                    ? <a href={`tel:${c.phone}`} className="w-full sm:w-auto"><Button variant="outline" className="h-9 w-full sm:w-auto justify-center text-sm" data-testid={`call-${c.id}`}><Phone className="w-4 h-4 mr-1.5" />Call</Button></a>
                    : <span title="No phone number on file" className="w-full sm:w-auto"><Button variant="outline" disabled className="h-9 w-full sm:w-auto justify-center text-sm" data-testid={`call-${c.id}`}><Phone className="w-4 h-4 mr-1.5" />Call</Button></span>}
                  {c.email
                    ? <a href={`mailto:${c.email}`} className="w-full sm:w-auto"><Button variant="outline" className="h-9 w-full sm:w-auto justify-center text-sm" data-testid={`email-${c.id}`}><Mail className="w-4 h-4 mr-1.5" />Email</Button></a>
                    : <span title="No email on file" className="w-full sm:w-auto"><Button variant="outline" disabled className="h-9 w-full sm:w-auto justify-center text-sm" data-testid={`email-${c.id}`}><Mail className="w-4 h-4 mr-1.5" />Email</Button></span>}
                  <Button variant="outline" className="h-9 w-full sm:w-auto justify-center text-sm" data-testid={`edit-${c.id}`} onClick={() => setEditItem(c)}>
                    <Pencil className="w-4 h-4 mr-1.5" />Edit
                  </Button>
                  {c.application_status === "Suspended"
                    ? <Button variant="outline" className="h-9 w-full sm:w-auto justify-center text-sm col-span-2 sm:col-span-1 border-green-300 text-green-700 hover:bg-green-50 hover:text-green-800" data-testid={`activate-${c.id}`} onClick={() => changeStatus(c, "Active")}><UserCog className="w-4 h-4 mr-1.5" />Reactivate</Button>
                    : <Button variant="outline" className="h-9 w-full sm:w-auto justify-center text-sm col-span-2 sm:col-span-1 border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800" data-testid={`suspend-${c.id}`} onClick={() => changeStatus(c, "Suspended")}><UserCog className="w-4 h-4 mr-1.5" />Suspend</Button>}
                </div>
              </div>
            ))}
          </div>
        )}

      <AddContractorDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
      <AddContractorDialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)} existing={editItem} onSaved={load} />
      {assignItem && <AssignJobToContractorDialog open={!!assignItem} onOpenChange={(o) => !o && setAssignItem(null)} contractor={assignItem} onDone={load} />}
    </DashboardLayout>
  );
}

const Mini = ({ l, v }) => <div className="bg-slate-50 rounded-lg py-1.5"><div className="text-[10px] uppercase tracking-wide text-slate-400">{l}</div><div className="text-sm font-semibold text-slate-800">{v}</div></div>;

const assignDisabledReason = (c) => {
  if (c.application_status === "Applicant") return "Cannot assign — contractor is still an Applicant";
  if (c.application_status === "Suspended") return "Cannot assign — contractor is suspended";
  if (!["Approved", "Active"].includes(c.application_status)) return `Cannot assign — status is ${c.application_status}`;
  if ((c.availability_status || "Available") === "Unavailable") return "Cannot assign — contractor is unavailable";
  if ((c.missing_docs || []).length > 0) return `Cannot assign — missing required documents: ${c.missing_docs.join(", ")}`;
  return "";
};

function FilterSelect({ testid, value, onChange, placeholder, options, labelFn }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-auto min-w-[130px]"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-64">
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{labelFn ? labelFn(o) : o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
